import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { Lines, EASE } from "../motion";
import { Cta } from "../ui-bits";

const Badge = () => (
  <div className="absolute -bottom-10 -left-6 z-10 h-28 w-28 md:-bottom-12 md:-left-14 md:h-32 md:w-32" aria-hidden="true">
    <svg viewBox="0 0 100 100" className="h-full w-full animate-spinslow">
      <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <circle cx="50" cy="50" r="49" fill="#dbfff8" />
      <text fontSize="7.6" letterSpacing="1.6" fill="#055860" fontFamily="Raleway" fontWeight="600"><textPath href="#circ" textLength="236" lengthAdjust="spacing">STRATEGIA · PRODUZIONE · GESTIONE · </textPath></text>
    </svg>
    <img src="/img/mark-dark.png" alt="" className="absolute left-1/2 top-1/2 h-9 w-auto -translate-x-1/2 -translate-y-1/2 md:h-11" />
  </div>
);

const TiltImage = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 80, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <motion.div
          className="relative aspect-[4/5] overflow-hidden bg-ink/10"
          initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.5, ease: EASE, delay: 0.8 }}
        >
          <motion.img
            src="/img/hero-team-v5.webp"
            alt="Vista dall'alto di un tavolo di lavoro con il team tra strategia, montaggio Reel, fotografia, ADV e newsletter"
            className="h-full w-full object-cover"
            initial={{ scale: 1.25 }}
            animate={{ scale: 1.02 }}
            transition={{ duration: 2.2, ease: EASE, delay: 0.8 }}
            fetchPriority="high"
          />
        </motion.div>
      </motion.div>
      <Badge />
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  return (
    <section ref={ref} className="relative px-5 pb-24 pt-32 sm:px-10 md:pt-40 lg:px-16 lg:pb-36" data-testid="home-hero">
      <div className="grid grid-cols-12 items-start gap-y-20 lg:gap-x-10">
        <motion.div className="col-span-12">
          <motion.p className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.8 }}>
            Studio creativo in Lombardia · Como, Monza e Brianza, Varese, Lecco
          </motion.p>
          <h1 className="mt-8 font-serif leading-[1.04] tracking-[-0.02em]" data-testid="hero-claim">
            <span className="sr-only">La tua comunicazione merita molto più di un bel contenuto.</span>
            <span aria-hidden="true" className="block text-[2.6rem] sm:text-6xl md:hidden">
              <Lines as="span" onLoad delay={0.7} lines={["La tua", "comunicazione"]} />
              <Lines as="span" onLoad delay={0.92} lineClass="text-teal" className="block" lines={["merita molto più", "di un bel", "contenuto."]} />
            </span>
            <span aria-hidden="true" className="hidden md:block md:text-[4rem] lg:text-[5.6rem] xl:text-[6.4rem]">
              <Lines as="span" onLoad delay={0.7} lines={["La tua comunicazione"]} />
              <Lines as="span" onLoad delay={0.85} lineClass="text-teal lg:pl-[12%]" className="block" lines={["merita molto più", "di un bel contenuto."]} />
            </span>
          </h1>
          <motion.div
            className="mt-14 flex max-w-xl flex-col gap-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 1.5 }}
          >
            <div>
              <p className="font-serif text-xl leading-snug md:text-2xl" data-testid="hero-lead">
                Strategia, creatività e produzione. Tutto parte da ciò che rende unico il tuo brand.
              </p>
              <p className="mt-5 text-base leading-relaxed text-ink/75" data-testid="hero-intro">
                Dietro ogni contenuto c'è molto più di quello che vedi. C'è una strategia, uno studio del pubblico, un'idea da sviluppare. Ci sono persone che scrivono, progettano, fotografano, girano video e trasformano tutto questo in comunicazione. Noi ci occupiamo di mettere insieme ogni pezzo.
              </p>
              <p className="mt-4 text-base leading-relaxed text-ink/75">
                B Digital Studio nasce dalla volontà di offrire alle aziende un supporto che vada oltre la semplice gestione dei social: un team creativo e operativo con cui confrontarsi, progettare e crescere.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Cta to="/progetti" testid="hero-cta-projects">Scopri i nostri progetti</Cta>
            </div>
          </motion.div>
        </motion.div>
        <motion.div
          className="col-span-12 hidden self-end lg:col-span-5 lg:block"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
        >
          <div className="flex items-center gap-4 text-xs uppercase tracking-[0.22em] text-ink/70">
            <span className="relative h-16 w-px overflow-hidden bg-ink/15"><motion.span className="absolute inset-x-0 top-0 h-6 bg-teal" animate={{ y: [-24, 64] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} /></span>
            Scorri per conoscere lo studio
          </div>
        </motion.div>
        <motion.div style={{ y: yImg }} className="col-span-10 col-start-2 sm:col-span-7 sm:col-start-5 lg:col-span-4 lg:col-start-9 xl:col-span-3 xl:col-start-10 xl:-mt-[22rem]">
          <TiltImage />
        </motion.div>
      </div>
    </section>
  );
};
